(() => {
  const getModalElements = () => ({
    modal: document.getElementById('CartNotificationModal'),
    overlay: document.getElementById('CartNotificationOverlay'),
    closeBtn: document.getElementById('CartNotificationClose'),
    imgEl: document.getElementById('CartNotificationImage'),
    thumbEl: document.getElementById('CartNotificationThumb'),
    typeEl: document.getElementById('CartNotificationType'),
    titleEl: document.getElementById('CartNotificationTitle'),
    moodEl: document.getElementById('CartNotificationMood'),
    priceEl: document.getElementById('CartNotificationPrice')
  });

  let hideTimeout = null;

  const PREORDER_TAGS = ['pre-order', 'pre order', 'preorder'];
  const productPreorderCache = new Map();

  const isPreorderTag = (tag) => PREORDER_TAGS.includes(String(tag || '').trim().toLowerCase());

  const mixMessage = () => {
    const modal = document.getElementById('CartNotificationModal');
    return modal?.dataset.mixMessage || 'Pre-order items can’t be checked out together with ready-stock items.';
  };

  const showMixNotice = (anchor) => {
    const message = mixMessage();
    const card = anchor.closest('.collection-tabs-card');
    const isQuickAdd = anchor.classList.contains('collection-tabs-card__quick-add-btn');
    const host = isQuickAdd && card
      ? card.querySelector('.collection-tabs-card__content')
      : anchor.parentElement;
    if (!host) return;

    let notice = host.querySelector(':scope > .cart-mix-notice');
    if (!notice) {
      notice = document.createElement('p');
      notice.className = 'cart-mix-notice';
      notice.setAttribute('role', 'status');
    }

    notice.textContent = message;
    notice.hidden = false;

    if (isQuickAdd) {
      host.prepend(notice);
    } else {
      anchor.insertAdjacentElement('afterend', notice);
    }
  };

  const clearMixNotice = (anchor) => {
    const scope = anchor.closest('form, .collection-tabs-card, .pdp-sticky-bar') || anchor.parentElement;
    scope?.querySelectorAll('.cart-mix-notice').forEach((notice) => notice.remove());
  };

  const productIsPreorder = async (handle) => {
    if (!handle) return false;
    if (productPreorderCache.has(handle)) return productPreorderCache.get(handle);

    try {
      const response = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) {
        productPreorderCache.set(handle, false);
        return false;
      }
      const product = await response.json();
      const isPreorder = (product.tags || []).some(isPreorderTag);
      productPreorderCache.set(handle, isPreorder);
      return isPreorder;
    } catch (error) {
      productPreorderCache.set(handle, false);
      return false;
    }
  };

  const cartConflictsWith = async (incomingIsPreorder) => {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) return false;

    const cart = await response.json();
    if (!cart.items || cart.items.length === 0) return false;

    const handles = [...new Set(cart.items.map((item) => item.handle))];
    const flags = await Promise.all(handles.map(productIsPreorder));
    const byHandle = {};
    handles.forEach((handle, index) => {
      byHandle[handle] = flags[index];
    });

    const hasPreorder = cart.items.some((item) => byHandle[item.handle]);
    const hasReady = cart.items.some((item) => !byHandle[item.handle]);

    return (incomingIsPreorder && hasReady) || (!incomingIsPreorder && hasPreorder);
  };

  window.openCartNotification = function(data) {
    const { modal, imgEl, thumbEl, typeEl, titleEl, moodEl, priceEl } = getModalElements();
    if (!modal || !data) return;

    if (imgEl) {
      imgEl.src = data.image || '';
      imgEl.alt = data.title || '';
    }
    if (thumbEl) {
      thumbEl.src = data.image || '';
      thumbEl.alt = data.title || '';
    }
    if (typeEl) typeEl.textContent = data.type || '';
    if (titleEl) titleEl.textContent = data.title || '';
    if (moodEl) moodEl.textContent = data.mood ? `Mood: ${data.mood}` : '';
    if (priceEl) priceEl.innerHTML = data.price || '';

    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-notification-locked');

    if (hideTimeout) clearTimeout(hideTimeout);
    hideTimeout = setTimeout(() => {
      window.closeCartNotification();
    }, 5000);
  };

  window.closeCartNotification = function() {
    const { modal } = getModalElements();
    if (modal) {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
    }
    document.body.classList.remove('cart-notification-locked');
    if (hideTimeout) clearTimeout(hideTimeout);
  };

  // Close handlers with delegation
  document.addEventListener('click', (e) => {
    if (e.target.closest('#CartNotificationClose') || e.target.closest('#CartNotificationOverlay')) {
      window.closeCartNotification();
    }
  });

  document.addEventListener('keydown', (e) => {
    const { modal } = getModalElements();
    if (e.key === 'Escape' && modal && modal.classList.contains('is-open')) {
      window.closeCartNotification();
    }
  });

  // Global submit interceptor: e.preventDefault() on all Product Add to Cart forms
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[action*="/cart/add"], form.pdp-form, form.pdp-sticky-bar__form, #PdpProductForm, #PdpStickyForm');
    if (!form) return;

    // Prevent default standard browser page navigation
    e.preventDefault();

    const submitBtn = form.querySelector('[type="submit"]') || form.querySelector('button[name="add"]');
    if (submitBtn) submitBtn.disabled = true;

    const formData = new FormData(form);
    const incomingIsPreorder = form.dataset.preorderTag === 'true';

    try {
      if (await cartConflictsWith(incomingIsPreorder)) {
        if (submitBtn) showMixNotice(submitBtn);
        return;
      }

      clearMixNotice(form);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const item = await response.json();

        // Live update header cart count
        if (window.updateHeaderCartCount) {
          window.updateHeaderCartCount();
        }

        // Fetch PDP specific info
        let moodText = '';
        const moodElement = document.querySelector('.pdp-info__mood');
        if (moodElement) {
          moodText = moodElement.textContent.replace(/^Mood:\s*/i, '').trim();
        }

        let typeText = '';
        const typeElement = document.querySelector('.pdp-info__category');
        if (typeElement) {
          typeText = typeElement.textContent.trim();
        }

        const pdpMainImg = document.getElementById('PdpMainImage');
        const productImage = item.image || (pdpMainImg ? pdpMainImg.src : '');

        // Format money according to shop settings
        const priceVal = item.final_price || item.price;
        let priceFormatted = '';
        if (priceVal) {
          if (window.theme && typeof window.theme.formatMoney === 'function') {
            priceFormatted = window.theme.formatMoney(priceVal);
          } else {
            priceFormatted = `IDR ${new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(priceVal / 100)}`;
          }
        }

        // Open popup
        window.openCartNotification({
          image: productImage,
          type: typeText || item.product_type || '',
          title: item.product_title || item.title || '',
          mood: moodText,
          price: priceFormatted
        });
      } else {
        const err = await response.json();
        alert(err.description || err.message || 'Could not add to cart');
      }
    } catch (err) {
      console.error('Error adding to cart', err);
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  });

  // Global click interceptor: e.preventDefault() on Quick Add buttons on product cards
  document.addEventListener('click', async (e) => {
    const quickAddBtn = e.target.closest('.collection-tabs-card__quick-add-btn');
    if (!quickAddBtn || quickAddBtn.disabled) return;

    e.preventDefault();
    const variantId = quickAddBtn.dataset.variantId;
    if (!variantId) return;

    const incomingIsPreorder = quickAddBtn.dataset.preorderTag === 'true';
    quickAddBtn.disabled = true;
    const card = quickAddBtn.closest('.collection-tabs-card');

    try {
      if (await cartConflictsWith(incomingIsPreorder)) {
        showMixNotice(quickAddBtn);
        return;
      }

      clearMixNotice(quickAddBtn);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          ...(quickAddBtn.dataset.preOrder === 'true' ? { properties: { 'Pre-order': 'Yes' } } : {})
        })
      });

      if (response.ok) {
        const item = await response.json();

        if (window.updateHeaderCartCount) {
          window.updateHeaderCartCount();
        }

        const cardImg = card ? card.querySelector('.collection-tabs-card__image--primary') : null;
        const cardType = card ? card.querySelector('.collection-tabs-card__type') : null;
        const cardTitle = card ? card.querySelector('.collection-tabs-card__title') : null;
        const cardPrice = card ? card.querySelector('.collection-tabs-card__price') : null;

        const priceVal = item.final_price || item.price;
        let priceFormatted = cardPrice ? cardPrice.innerHTML : '';
        if (priceVal) {
          if (window.theme && typeof window.theme.formatMoney === 'function') {
            priceFormatted = window.theme.formatMoney(priceVal);
          } else {
            priceFormatted = `IDR ${new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(priceVal / 100)}`;
          }
        }

        window.openCartNotification({
          image: item.image || (cardImg ? cardImg.src : ''),
          type: (cardType ? cardType.textContent.trim() : '') || item.product_type || '',
          title: item.product_title || (cardTitle ? cardTitle.textContent.trim() : '') || '',
          mood: '',
          price: priceFormatted
        });
      }
    } catch (err) {
      console.error('Failed to quick add to cart', err);
    } finally {
      quickAddBtn.disabled = false;
    }
  });
})();
